import { Component, Input, OnInit, OnDestroy } from "@angular/core";
import { firstValueFrom, Observable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { AsyncPipe, NgClass, NgFor, NgIf } from "@angular/common";
import { LocalizationModule } from "../localization";
import { MatMenuModule } from "@angular/material/menu";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { User } from "src/app/shared/models/user.model";
import { Select, Store } from "@ngxs/store";
import { UserState } from "src/app/store/user/user.store";
import { IconEvents } from "src/assets/icons/events";
import { IconFlag } from "src/assets/icons/flag";
import { IconStudios } from "src/assets/icons/studios";
import { IconSignOut } from "src/assets/icons/sign-out";
import { home } from "src/assets/icons/home";
import { SafePipe } from "../../shared/pipes/safe.pipe";
import { SafeTypes } from "../../shared/constants/safe-types.enum";
import { MatDividerModule } from "@angular/material/divider";
import { MatDialog, MatDialogModule } from "@angular/material/dialog";
import { UserMeetDialogComponent } from "../user-dialog/meet/user-meet-dialog.component";
import { AuthContract } from "../../shared/contracts/auth.contract";
import { AuthTokenInfo } from "../../shared/models/auth-token.model";
import {
  LoginByToken,
  Logout,
  ToggleSidePanel,
} from "../../store/user/user.actions";
import { NotificationTypes } from "../../shared/constants/notification-types.enum";
import { ToastService } from "../../shared/services/toast.service";
import { TranslocoService } from "@ngneat/transloco";
import { UserEventDialogComponent } from "../user-dialog/event/user-event-dialog.component";
import { Router } from "@angular/router";
import { IconLoginAs } from "../../../assets/icons/login-as";
import { LoginAsDialogComponent } from "../dialogs/login-as/login-as-dialog.component";
import { environment } from "src/environments/environment";
import { ApiService } from "src/app/shared/constants/api.service";
import { AAUAccountMembershipService } from "src/app/shared/constants/aau-account-membership.service";
import { LEAGUE_ID } from "../aau-district-directors/aau-district-directors.service";

@Component({
  selector: "app-user-actions",
  templateUrl: "./user-actions.component.html",
  styleUrls: ["./user-actions.component.scss"],
  standalone: true,
  imports: [
    LocalizationModule,
    MatButtonModule,
    MatMenuModule,
    MatIconModule,
    SafePipe,
    NgFor,
    MatDividerModule,
    MatDialogModule,
    NgClass,
    NgIf,
    AsyncPipe,
  ],
  providers: [ToastService],
})
export class UserActionsComponent implements OnInit, OnDestroy {
  @Select(UserState.getPrimaryRoleName)
  public getRoleName$!: Observable<string>;

  @Input() user!: User;

  @Select(UserState.isAdmin)
  isAdmin$!: Observable<boolean>;

  @Select(UserState.isLeagueAdmin)
  isLeagueAdmin$!: Observable<boolean>;

  @Select(UserState.isLoginAs)
  public isLoginAs$!: Observable<boolean>;

  public isAdmin: boolean | null = null;

  @Select(UserState.isDashboard)
  public isDashboard$!: Observable<boolean>;

  public IconEvents = IconEvents;
  public IconFlag = IconFlag;
  public IconStudios = IconStudios;
  public IconSignOut = IconSignOut;
  public IconLoginAs = IconLoginAs;
  public IconHome = home;
  public SafeTypes = SafeTypes;

  public isDashboard = false;

  private unsubscriber$ = new Subject<void>();

  accountHolderView = false;
  isAAUAccount: boolean = false;
  isAAULeagueAdmin: boolean | null = false;
  isAAUMembershipDepartment: boolean | null = false;

  leagueId: number = -1;

  constructor(
    private router: Router,
    private authContract: AuthContract,
    private store: Store,
    private toast: ToastService,
    private transloco: TranslocoService,
    private aauAccountMebershipsService: AAUAccountMembershipService,
    public dialog: MatDialog
  ) { }

  public async ngOnInit() {
    this.checkAccountHolder(this.user);

    this.isDashboard$.pipe(takeUntil(this.unsubscriber$)).subscribe((isDashboard: any) => {
      this.isDashboard = isDashboard;
    });

    this.isAdmin$.pipe(takeUntil(this.unsubscriber$)).subscribe((isAdmin) => {
      this.isAdmin = isAdmin;
      this.checkAccountHolder(this.user);
    });

    this.isLeagueAdmin$.pipe(takeUntil(this.unsubscriber$)).subscribe((isLeagueAdmin) => {
      this.checkAAULeagueAdmin(isLeagueAdmin, this.user);
    });

    this.aauAccountMebershipsService.haveSomeAccount();
    this.aauAccountMebershipsService.aauAccountPerson$.pipe(takeUntil(this.unsubscriber$)).subscribe(
      (isAAUAccount) => {
        this.isAAUAccount = isAAUAccount;
        this.checkAccountHolder(this.user);
      }
    );

    this.checkMemberskipDepartment(this.user);
  }

  public ngOnDestroy() {
    this.unsubscriber$.next();
    this.unsubscriber$.complete();
  }


  manageAccount() {
    const authToken = this.store.selectSnapshot(UserState.authToken);
    const token = authToken && authToken.token ? authToken.token : null;

    let returnUrl = encodeURIComponent(window.location.href).replaceAll(
      "%2F",
      "___"
    );
    const url = `${environment.EVENT_PORTAL_URL}#account;applicationMode=SPORTSHUB;sportsHubUrl=${returnUrl};token=${token}`;
    window.location.href = url;
  }

  checkAccountHolder(user: User) {
    if (!this.isAdmin) {
      if (
        user &&
        user.eventAccount &&
        user.eventAccount.accessLevel &&
        user.eventAccount.accessLevel === "USER"
      ) {
        this.accountHolderView = true;
      }
    }
  }

  checkMemberskipDepartment(u: any) {
    if (u && u.eventAccount && u.eventAccount.roleList?.length) {
      const foundAAULeagueAdmin = u.eventAccount.roleList.find(
        (role: any) => {
          if (
            role.name === "Membership Department" &&
            role.leagues?.length &&
            (role.leagues.find((league: any) => league.id === LEAGUE_ID.AAU))
          ) {
            this.leagueId = LEAGUE_ID.AAU;
            return true;
          }
          return false;
        }
      );
      this.isAAUMembershipDepartment = !!foundAAULeagueAdmin;
    } else {
      this.isAAUMembershipDepartment = false;
    }
  }

  checkAAULeagueAdmin(isLeagueAdmin: boolean | null, u: any) {
    if (isLeagueAdmin) {
      if (u && u.eventAccount && u.eventAccount.roleList?.length) {
        const foundAAULeagueAdmin = u.eventAccount.roleList.find(
          (role: any) => {
            if (
              role.name === "League Admin" &&
              role.leagues?.length &&
              (role.leagues.find((league: any) => league.id === LEAGUE_ID.AAU) || role.leagues.find((league: any) => league.id === LEAGUE_ID.USAGR))
            ) {
              this.leagueId = role.leagues.find((league: any) => league.id === LEAGUE_ID.AAU)?.id || role.leagues.find((league: any) => league.id === LEAGUE_ID.USAGR)?.id;
              return true;
            }
            return false;
          }
        );
        this.isAAULeagueAdmin = !!foundAAULeagueAdmin;
      } else {
        this.isAAULeagueAdmin = false;
      }
    } else {
      this.isAAULeagueAdmin = false;
    }
    this.checkAccountHolder(u);
  }

  openMeetDialog(
    enterAnimationDuration: string,
    exitAnimationDuration: string
  ): void {
    const dialogRef = this.dialog.open(UserMeetDialogComponent);

    dialogRef.afterClosed().subscribe((result) => {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "smooth",
      });
    });
  }

  openEventDialog(
    enterAnimationDuration: string,
    exitAnimationDuration: string
  ): void {
    const dialogRef = this.dialog.open(UserEventDialogComponent);

    dialogRef.afterClosed().subscribe((result) => {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "smooth",
      });
    });
  }

  handleLoginAs() {
    const dialogRef = this.dialog.open(LoginAsDialogComponent, {
      width: "460px",
      panelClass: "custom-dialog-container",
      data: {
        transloco: this.transloco,
        leagueId: this.leagueId
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.aauAccountMebershipsService.haveSomeAccount();
        this.aauAccountMebershipsService.aauAccountPerson$.subscribe(
          (isAAUAccount) => {
            this.isAAUAccount = isAAUAccount;
            this.checkAccountHolder(this.user);
          }
        );
      }
    });
  }

  public async endSession(): Promise<void> {
    const authToken = await this.authContract.endSession({}).toPromise();
    if (authToken && authToken.accessToken) {
      await this.store
        .dispatch(new LoginByToken(authToken.accessToken))
        .toPromise();
    }
    const currentUser = this.store.selectSnapshot(UserState.authUser);
    const isLeagueAdmin = this.store.selectSnapshot(UserState.isLeagueAdmin);
    this.checkAAULeagueAdmin(isLeagueAdmin, currentUser || this.user);
    this.aauAccountMebershipsService.haveSomeAccount();
  }

  logout() {
    this.authContract.logout().subscribe({
      next: (value: AuthTokenInfo) => {
        this.store.dispatch(new Logout());
        this.router.navigateByUrl("");
      },
      error: (error: any) => {
        const body = this.transloco.translate("ALERT_LOGOUT_FAILED");
        this.toast.show({ body, type: NotificationTypes.error });
      },
    });
  }

  dashboard() {
    this.router.navigateByUrl("dashboard");
  }
}
